import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLocationContext } from '../hooks/useLocationContext';
import { useScopedPath } from '../components/ScopedLink';
import {
  useGetClassesQuery,
  useGetClassInstancesQuery,
  useGetClassSessionsQuery,
  useRemoveClassOverrideMutation,
  useGetClassStudentsQuery,
  useLocalGetLocationsQuery,
  useLazyGetSessionForClassDateQuery,
} from '../store/api/localApi';
import RoleGate from '../components/RoleGate';
import TermManagementPanel from './classes/TermManagementPanel';
import CloseClassDialog from './classes/CloseClassDialog';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CalendarDaysIcon,
  ClockIcon,
  XCircleIcon,
  ArrowPathIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/outline';
import { useToast } from '../components/Toast';
import { getErrorMessage } from '../utils/errors';
import { dublinDateString } from '../utils/date';
import CreateClassModal from './classes/CreateClassModal';
import RolloverWizard from './classes/RolloverWizard';
import EditClassModal from './classes/EditClassModal';
import OverrideModal from './classes/OverrideModal';
import EnrollModal from './classes/EnrollModal';
import EnrolledStudentsSection from './classes/EnrolledStudentsSection';
import AssignedStaffSection from './classes/AssignedStaffSection';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES_FULL = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const Classes: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [fetchSessionForClassDate] = useLazyGetSessionForClassDateQuery();
  const [openingSession, setOpeningSession] = useState(false);
  const { activeOrganizationId, isAdmin, isOrgLevel, isLocationAdmin } = useAuth();
  const { effectiveLocationId: activeLocationId } = useLocationContext();
  const scopedPath = useScopedPath();
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showRollover, setShowRollover] = useState(false);
  const [selectedLocationFilter, setSelectedLocationFilter] = useState<string>(
    activeLocationId || ''
  );

  // For org-level roles, load locations to pick from. Skip when no
  // orgId yet — happens for a render frame on page mount before
  // useLocationContext settles.
  const { data: locations } = useLocalGetLocationsQuery(activeOrganizationId || '', {
    skip: !isOrgLevel || !activeOrganizationId,
  });

  // Use filtered location for class query - org admins pick a location, location admins have one
  const effectiveLocationId = isOrgLevel ? selectedLocationFilter : activeLocationId;

  const { data: classes, isLoading } = useGetClassesQuery(effectiveLocationId || '', {
    skip: !effectiveLocationId,
  });

  const selectedClassData = classes?.find((c) => c.id === selectedClass);

  // Look ahead 90 days for upcoming sessions. A 2-week window went dark during
  // a between-terms gap (e.g. the summer break): with no term covering the next
  // 13 days the panel showed "no sessions" even though the next term's sessions
  // were only a few days further out. 90 days clears any realistic gap; the
  // rendered list is capped below so the panel stays short.
  const dateRange = useMemo(() => {
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 90);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  }, []);

  const { data: instances } = useGetClassInstancesQuery(
    { classId: selectedClass || '', ...dateRange },
    { skip: !selectedClass }
  );

  // Real session rows for the same range, so each instance row can show its
  // LIVE status (started / finished) rather than always offering "Attendance".
  // The class-instances feed above is schedule-only.
  const { data: classSessions } = useGetClassSessionsQuery(
    { classId: selectedClass || '', from: dateRange.startDate, to: dateRange.endDate },
    { skip: !selectedClass }
  );
  const sessionByDate = useMemo(() => {
    const map = new Map<string, { id: string; status: string }>();
    for (const s of classSessions?.sessions ?? []) {
      map.set(s.date, { id: s.id, status: s.status });
    }
    return map;
  }, [classSessions]);

  const { data: rosterData } = useGetClassStudentsQuery(selectedClass || '', {
    skip: !selectedClass,
  });

  // "Run today's session" only makes sense on a day this class actually runs —
  // i.e. today falls inside a term AND is a scheduled weekday (not cancelled).
  // The instances feed already encodes exactly that (a between-terms gap day has
  // no instance), so today having a non-cancelled instance is the gate. Without
  // it the green button showed on every day and just errored on gap/off days.
  const todayStr = dublinDateString(new Date());
  const hasSessionToday = (instances ?? []).some(
    (inst) => inst.date === todayStr && inst.status !== 'CANCELLED'
  );

  if (isLoading) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Classes
            </h2>
            <p className="mt-1 text-sm text-gray-500">Manage class schedules and sessions</p>
          </div>
          <RoleGate when={isAdmin}>
            <div className="mt-4 flex gap-3 md:mt-0 md:ml-4">
              <button
                data-testid="rollover-start"
                disabled={!effectiveLocationId}
                onClick={() => setShowRollover(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <ArrowPathIcon className="-ml-1 mr-2 h-5 w-5" />
                Year rollover
              </button>
              <button
                data-testid="add-class-btn"
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Add Class
              </button>
            </div>
          </RoleGate>
        </div>

        {/* Location filter for org-level roles */}
        {isOrgLevel && locations && locations.length > 0 && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <select
              data-testid="location-filter"
              value={selectedLocationFilter}
              onChange={(e) => {
                setSelectedLocationFilter(e.target.value);
                setSelectedClass(null);
              }}
              className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              <option value="">Select a location...</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Class List */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <ul className="divide-y divide-gray-200">
                {classes
                  ?.filter((c) => c.isActive)
                  .map((cls) => (
                    <li key={cls.id}>
                      <button
                        data-testid={`class-${cls.id}`}
                        onClick={() => setSelectedClass(cls.id)}
                        className={`w-full text-left px-4 py-4 hover:bg-gray-50 ${
                          selectedClass === cls.id
                            ? 'bg-primary-50 border-r-4 border-primary-500'
                            : ''
                        }`}
                      >
                        <p className="text-sm font-medium text-gray-900">{cls.name}</p>
                        <p className="text-xs text-gray-500 flex items-center mt-1">
                          <ClockIcon className="h-3 w-3 mr-1" />
                          {cls.startTime} - {cls.endTime}
                        </p>
                        <div className="flex gap-1 mt-1">
                          {cls.daysOfWeek?.map((d) => (
                            <span
                              key={d}
                              className="inline-flex items-center px-1.5 py-0 rounded text-[10px] font-medium bg-gray-100 text-gray-600"
                            >
                              {DAY_NAMES[d]}
                            </span>
                          ))}
                        </div>
                      </button>
                    </li>
                  ))}
              </ul>

              {(!classes || classes.filter((c) => c.isActive).length === 0) && (
                <div className="text-center py-8 px-4">
                  <CalendarDaysIcon className="mx-auto h-10 w-10 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">No classes yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Class Detail + Schedule */}
          <div className="lg:col-span-2 space-y-6" data-testid="class-detail">
            {selectedClassData ? (
              <>
                {/* Class Info */}
                <div className="bg-white shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        {selectedClassData.name}
                      </h3>
                      <div className="flex gap-2">
                        {hasSessionToday && (
                          <button
                            data-testid="run-todays-session"
                            disabled={openingSession}
                            onClick={async () => {
                              if (!selectedClassData) return;
                              setOpeningSession(true);
                              try {
                                // Dublin date, not UTC. `session.date` is a Dublin
                                // calendar date; during IST a raw toISOString()
                                // still reads as yesterday between 00:00-00:59
                                // local, which would open the wrong day's session.
                                const today = dublinDateString(new Date());
                                const result = await fetchSessionForClassDate({
                                  classId: selectedClassData.id,
                                  date: today,
                                }).unwrap();
                                navigate(scopedPath(`/sessions/${result.session.id}`));
                              } catch (err) {
                                // Must be surfaced: on a non-scheduled day the
                                // API returns CLASS_NOT_SCHEDULED_ON_DATE, and a
                                // silent catch makes the button read as broken.
                                showError(getErrorMessage(err));
                                setOpeningSession(false);
                              }
                            }}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-sm text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                          >
                            <PlayCircleIcon className="h-4 w-4 mr-1" />
                            {openingSession ? 'Opening…' : "Run today's session"}
                          </button>
                        )}
                        <RoleGate when={isAdmin}>
                          <button
                            data-testid="edit-class-btn"
                            onClick={() => setShowEditModal(true)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <PencilIcon className="h-4 w-4 mr-1" /> Edit
                          </button>
                          <button
                            data-testid="close-class-btn"
                            onClick={() => setShowCloseDialog(true)}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-sm text-white bg-red-600 hover:bg-red-700"
                          >
                            <TrashIcon className="h-4 w-4 mr-1" /> Close
                          </button>
                        </RoleGate>
                      </div>
                    </div>

                    {selectedClassData.description && (
                      <p className="text-sm text-gray-500 mb-4">{selectedClassData.description}</p>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <h5 className="text-xs font-medium text-gray-500 uppercase">Schedule</h5>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedClassData.daysOfWeek?.map((d) => DAY_NAMES_FULL[d]).join(', ')}
                        </p>
                      </div>
                      <div>
                        <h5 className="text-xs font-medium text-gray-500 uppercase">Time</h5>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedClassData.startTime} - {selectedClassData.endTime}
                        </p>
                      </div>
                      {selectedClassData.capacity && (
                        <div>
                          <h5 className="text-xs font-medium text-gray-500 uppercase">Capacity</h5>
                          <p className="mt-1 text-sm text-gray-900">
                            {selectedClassData.capacity} students
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Assigned Staff (admin sees + edits, staff sees read-only) */}
                <AssignedStaffSection
                  classId={selectedClass!}
                  locationId={selectedClassData.locationId}
                  isAdmin={isAdmin}
                />

                {/* Term management (§3) — admin-only */}
                <RoleGate when={isAdmin}>
                  <TermManagementPanel classId={selectedClass!} />
                </RoleGate>

                {/* Enrolled Students */}
                <EnrolledStudentsSection
                  classId={selectedClass!}
                  className={selectedClassData.name}
                  locationId={selectedClassData.locationId}
                  students={rosterData?.students || []}
                  count={rosterData?.count || 0}
                  isAdmin={isAdmin}
                  onEnroll={() => setShowEnrollModal(true)}
                  // Every other active class at this location is a possible
                  // transfer target; the current one obviously is not.
                  transferTargets={(classes || [])
                    .filter((c) => c.id !== selectedClass)
                    .map((c) => ({ id: c.id, name: c.name }))}
                  upcoming={rosterData?.upcoming || []}
                />

                {/* Upcoming Instances */}
                <div className="bg-white shadow rounded-lg" data-testid="upcoming-sessions">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900">Upcoming Sessions</h3>
                      <RoleGate when={isAdmin}>
                        <button
                          data-testid="cancel-modify-btn"
                          onClick={() => setShowOverrideModal(true)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <XCircleIcon className="h-4 w-4 mr-1" /> Cancel / Modify Day
                        </button>
                      </RoleGate>
                    </div>

                    <div className="space-y-2">
                      {instances?.slice(0, 10).map((inst) => {
                        const isToday = inst.date === new Date().toISOString().split('T')[0];
                        return (
                          <div
                            key={inst.date}
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              inst.status === 'CANCELLED'
                                ? 'border-red-200 bg-red-50'
                                : inst.status === 'MODIFIED'
                                ? 'border-yellow-200 bg-yellow-50'
                                : isToday
                                ? 'border-primary-200 bg-primary-50'
                                : 'border-gray-200'
                            }`}
                          >
                            <div>
                              <p
                                className={`text-sm font-medium ${
                                  inst.status === 'CANCELLED'
                                    ? 'text-red-700 line-through'
                                    : 'text-gray-900'
                                }`}
                              >
                                {DAY_NAMES_FULL[inst.dayOfWeek]},{' '}
                                {new Date(inst.date + 'T12:00:00').toLocaleDateString('en-IE', {
                                  day: 'numeric',
                                  month: 'short',
                                })}
                                {isToday && (
                                  <span className="ml-2 text-xs text-primary-600 font-medium">
                                    Today
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-gray-500">
                                {inst.startTime} - {inst.endTime}
                              </p>
                              {inst.reason && (
                                <p className="text-xs text-gray-500 italic mt-0.5">{inst.reason}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {inst.status !== 'CANCELLED' &&
                                (() => {
                                  const live = sessionByDate.get(inst.date);
                                  // A finished session must NOT offer a fresh
                                  // "Attendance" start — show it's done, with a
                                  // read-only View into its recap. A live one
                                  // opens directly. Otherwise (not yet started)
                                  // the lazy-create Attendance button as before.
                                  if (live?.status === 'ENDED') {
                                    return (
                                      <>
                                        <span
                                          data-testid={`session-status-${inst.classId}-${inst.date}`}
                                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                                        >
                                          Finished
                                        </span>
                                        <button
                                          onClick={() =>
                                            navigate(scopedPath(`/sessions/${live.id}`))
                                          }
                                          data-testid={`session-view-${inst.classId}-${inst.date}`}
                                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-700 hover:bg-gray-100"
                                          title="View this finished session"
                                        >
                                          View
                                        </button>
                                      </>
                                    );
                                  }
                                  if (live?.status === 'IN_PROGRESS') {
                                    return (
                                      <>
                                        <span
                                          data-testid={`session-status-${inst.classId}-${inst.date}`}
                                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800"
                                        >
                                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                          Live
                                        </span>
                                        <button
                                          onClick={() =>
                                            navigate(scopedPath(`/sessions/${live.id}`))
                                          }
                                          data-testid={`session-open-${inst.classId}-${inst.date}`}
                                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                          title="Open the live session"
                                        >
                                          Open session
                                        </button>
                                      </>
                                    );
                                  }
                                  return (
                                    <button
                                      onClick={async () => {
                                        try {
                                          const result = await fetchSessionForClassDate({
                                            classId: inst.classId,
                                            date: inst.date,
                                          }).unwrap();
                                          navigate(scopedPath(`/sessions/${result.session.id}`));
                                        } catch {
                                          // Lazy-get may fail if the date is out of range;
                                          // the inline disabled state in the row is enough.
                                        }
                                      }}
                                      data-testid={`session-open-${inst.classId}-${inst.date}`}
                                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100"
                                      title="Open this day's session"
                                    >
                                      Attendance
                                    </button>
                                  );
                                })()}
                              {inst.status === 'CANCELLED' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Cancelled
                                </span>
                              )}
                              {inst.status === 'MODIFIED' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Modified
                                </span>
                              )}
                              {inst.isOverridden && isAdmin && (
                                <RemoveOverrideButton classId={inst.classId} date={inst.date} />
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {(!instances || instances.length === 0) && (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No upcoming sessions
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-12 text-center">
                  <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Select a class</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Choose a class to view schedule and manage sessions.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {showCreateModal && (
          <CreateClassModal
            locationId={effectiveLocationId || ''}
            isLocationAdmin={isLocationAdmin}
            isOrgLevel={isOrgLevel}
            onClose={() => setShowCreateModal(false)}
          />
        )}
        {showEditModal && selectedClassData && (
          <EditClassModal cls={selectedClassData} onClose={() => setShowEditModal(false)} />
        )}
        {showOverrideModal && selectedClass && (
          <OverrideModal classId={selectedClass} onClose={() => setShowOverrideModal(false)} />
        )}

        {showEnrollModal && selectedClass && selectedClassData && effectiveLocationId && (
          <EnrollModal
            classId={selectedClass}
            className={selectedClassData.name}
            locationId={effectiveLocationId}
            roster={rosterData?.students || []}
            onClose={() => setShowEnrollModal(false)}
          />
        )}
        {showRollover && effectiveLocationId && (
          <RolloverWizard locationId={effectiveLocationId} onClose={() => setShowRollover(false)} />
        )}
        {showCloseDialog && selectedClassData && (
          <CloseClassDialog
            classId={selectedClassData.id}
            className={selectedClassData.name}
            onClose={() => setShowCloseDialog(false)}
            onClosed={() => {
              setShowCloseDialog(false);
              setSelectedClass(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

/** Remove override button */
const RemoveOverrideButton: React.FC<{ classId: string; date: string }> = ({ classId, date }) => {
  const [removeOverride] = useRemoveClassOverrideMutation();
  return (
    <button
      onClick={async () => {
        await removeOverride({ classId, date });
      }}
      className="p-1 text-gray-400 hover:text-gray-600"
      title="Restore default"
    >
      <ArrowPathIcon className="h-4 w-4" />
    </button>
  );
};

export default Classes;
